import * as React from 'react';
import {
  Box,
  Cluster,
  Copy,
  FormItem,
  Grid,
  Heading,
  Select,
  Spinner,
  Stack,
  TagList,
  TextInput,
  Toggle,
  ISelectOption,
  Toast,
  useValidateForm,
} from '@pluto-tv/assemble';

import {useAppPermissions} from 'app/permissions';

import CrudError from 'components/crudError';
import {useFindQuery as useFindGenresQuery} from 'features/genres/genresApi';
import {useFindQuery as useFindCategoriesQuery} from 'features/categories/categoriesApi';

import {INestedChannelSettingsProps} from '../nestedPropsInterface';
import {orderBy, sortBy} from 'lodash-es';
import {IGracenoteIntegration} from 'models/channels';
import {channelGracenoteIntegrationValidator} from 'views/programming/channel/validators';

export default ({
  model,
  setFields,
  onBlur,
  onChange,
  form,
  pristineModel,
  setIsGracenoteFormValid,
}: INestedChannelSettingsProps): JSX.Element => {
  const [seoAliasesList, setSeoAliasesList] = React.useState<ISelectOption[]>([]);

  const [subgenresOptions, setSubgenresOptions] = React.useState<ISelectOption[]>([]);
  const [subCategoriesOptions, setSubCategoriesOptions] = React.useState<ISelectOption[]>([]);

  const {data: genres, isFetching: isFetchingGenres, isError: isErrorGenres} = useFindGenresQuery();
  const {data: categories, isFetching: isFetchingCategories, isError: isErrorCategories} = useFindCategoriesQuery();

  const {
    form: gracenoteForm,
    model: gracenoteModel,
    state: gracenoteFormState,
    setModel: gracenoteISetModel,
    setFields: gracenoteSetFields,
    pristineModel: gracenotePristineModel,
  } = useValidateForm<IGracenoteIntegration>(channelGracenoteIntegrationValidator);

  React.useEffect(() => {
    if (seoAliasesList.length < (model.seoAliases || []).length) {
      const list = handleFilteredList(model.seoAliases || []);
      setSeoAliasesList(list);
    }
  }, [model.seoAliases, seoAliasesList.length]);

  React.useEffect(() => {
    gracenoteISetModel(pristineModel.gracenoteIntegration || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pristineModel.gracenoteIntegration]);

  React.useEffect(() => {
    if (model.autoSchedule && !model.episodesQueue?.length) {
      Toast.error(
        'One or more episodes must be available in the Channel Queue in order to enable the Auto Scheduling feature.',
      );
      setIsGracenoteFormValid(false);
    }
    setIsGracenoteFormValid(gracenoteFormState.isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.autoSchedule, setIsGracenoteFormValid]);

  React.useEffect(() => {
    if (gracenotePristineModel.enabled !== gracenoteModel.enabled) {
      // Setting values in case some of them are undefined to ensure the validations to run
      const gracenoteIntegration: IGracenoteIntegration = {
        enabled: !!gracenoteModel.enabled,
        dynamicClip: gracenoteModel.dynamicClip ? gracenoteModel.dynamicClip : gracenoteModel.enabled ? '' : undefined,
        genre: gracenoteModel.genre ? gracenoteModel.genre : gracenoteModel.enabled ? '' : undefined,
        subGenre: gracenoteModel.subGenre ? gracenoteModel.subGenre : gracenoteModel.enabled ? '' : undefined,
        category: gracenoteModel.category ? gracenoteModel.category : gracenoteModel.enabled ? '' : undefined,
        subCategory: gracenoteModel.subCategory ? gracenoteModel.subCategory : gracenoteModel.enabled ? '' : undefined,
        seriesType: gracenoteModel.enabled ? 'live' : undefined,
      };

      setFields({gracenoteIntegration});
      gracenoteSetFields(gracenoteIntegration);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gracenoteModel.enabled, gracenotePristineModel]);

  React.useEffect(() => {
    if (gracenoteModel.enabled && model.autoSchedule) {
      // NOT allow a user to enable both the Auto Schedule, and the Gracenote integration at the same time.
      // Only one, or the other can be enabled. Never together.
      Toast.error('Auto Schedule and Gracenote integration cannot be enabled at the same time');
    }
  }, [gracenoteModel.enabled, model.autoSchedule]);

  React.useEffect(() => {
    if (gracenoteModel.genre && genres?.length) {
      setSubgenresOptions(
        orderBy(
          genres.find(g => g.genre === gracenoteModel.genre)?.subGenres.map(subg => ({label: subg})) || [],
          'label',
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gracenoteModel.genre, genres]);

  React.useEffect(() => {
    if (gracenoteModel.category && categories?.length) {
      setSubCategoriesOptions(
        orderBy(
          categories.find(g => g.category === gracenoteModel.category)?.subCategories.map(subc => ({label: subc})) ||
            [],
          'label',
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gracenoteModel.category, categories]);

  React.useMemo(() => {
    setIsGracenoteFormValid(!(gracenoteModel.enabled && model.autoSchedule) && gracenoteFormState.isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gracenoteFormState.isValid]);

  const handleOnChangeGracenote = (items: IGracenoteIntegration) => {
    gracenoteSetFields(items as Partial<IGracenoteIntegration>);
    setFields({gracenoteIntegration: {...model.gracenoteIntegration, ...(items as Partial<IGracenoteIntegration>)}});
  };

  const handleFilteredList = (seoAliasesArray: string[]) => {
    const list = seoAliasesArray.map((el: any, index) => ({label: el, value: el, order: index}));
    return sortBy(list, 'label');
  };

  const {
    // ableTo,
    permissions,
  } = useAppPermissions();

  if (isErrorGenres || isErrorCategories) {
    return <CrudError error='Error loading page data' />;
  }

  if (isFetchingGenres || isFetchingCategories) {
    return (
      <Box fullHeight={true}>
        <Spinner center={true} minHeight='9.375rem' size='xlarge' />
      </Box>
    );
  }

  return (
    <Box borderTop={true} borderSize='0.125rem' borderColor='cavern'>
      <Grid maxCols={2} gap='xxxxxsmall'>
        <Box background='pewter'>
          <Box
            borderBottom={true}
            borderSize='0.25rem'
            borderColor='shadow'
            paddingTop={{mobile: 'medium', wide: 'large'}}
            paddingBottom={{mobile: 'medium', wide: 'xlarge'}}
            paddingX={{mobile: 'medium', wide: 'xlarge'}}
          >
            <Stack space='xlarge'>
              <Heading level='h3' color='secondary'>
                Auto Scheduling
              </Heading>
              <Cluster space='xxxlarge'>
                <FormItem
                  {...form.autoSchedule}
                  child='Toggle'
                  permission={permissions.CHANNEL_EDIT}
                  state={gracenoteModel.enabled && model.autoSchedule ? 'error' : undefined}
                >
                  <Toggle
                    label='Yes'
                    onChange={value => onChange('autoSchedule', value)}
                    value={model.autoSchedule}
                    id='autoScheduleToogle'
                  />
                </FormItem>
                <FormItem
                  {...form.autoScheduleDays}
                  required={model.autoSchedule}
                  onBlur={() => onBlur('autoScheduleDays')}
                  permission={permissions.CHANNEL_EDIT}
                >
                  <TextInput
                    id='autoScheduleDays'
                    type='number'
                    onChange={value => {
                      onChange('autoScheduleDays', value);
                    }}
                    value={model.autoScheduleDays}
                    maxLength={2}
                  />
                </FormItem>
              </Cluster>
            </Stack>
          </Box>
          <Box paddingY={{mobile: 'medium', wide: 'large'}} paddingX={{mobile: 'medium', wide: 'xlarge'}}>
            <Stack space='xlarge'>
              <Heading level='h3' color='secondary'>
                Configuration
              </Heading>
              <Stack space='small'>
                <FormItem {...form.seoAliases} permission={permissions.CHANNEL_EDIT}>
                  <TagList
                    value={model.seoAliases}
                    onChange={value =>
                      setFields({
                        seoAliases: orderBy([...new Set(value || [])] as string[], seoAliases =>
                          seoAliases.toLowerCase(),
                        ),
                      })
                    }
                    id='seoAliases'
                  />
                </FormItem>
                <FormItem {...form.tags} permission={permissions.CHANNEL_EDIT}>
                  <TagList
                    value={model.tags}
                    onChange={value =>
                      setFields({tags: orderBy([...new Set(value || [])] as string[], tag => tag.toLowerCase())})
                    }
                    id='tags'
                  />
                </FormItem>
                <FormItem
                  {...form.googleDai}
                  label='Google DAI + AES 128 Enabled'
                  child='Toggle'
                  permission={permissions.CHANNEL_EDIT}
                >
                  <Toggle
                    label='Yes'
                    onChange={value => onChange('googleDai', value)}
                    value={model.googleDai}
                    id='googleDai'
                  />
                </FormItem>
              </Stack>
            </Stack>
          </Box>
        </Box>
        <Box background='pewter'>
          <Box paddingY={{mobile: 'medium', wide: 'large'}} paddingX={{mobile: 'medium', wide: 'xlarge'}}>
            <Stack space='xxlarge'>
              <Stack space='xlarge'>
                <Heading level='h3' color='secondary'>
                  Gracenote Auto Publishing
                </Heading>
                <Stack space='small'>
                  <Cluster space='xxxlarge'>
                    <FormItem
                      {...gracenoteForm.enabled}
                      child='Toggle'
                      permission={permissions.CHANNEL_EDIT}
                      state={gracenoteModel.enabled && model.autoSchedule ? 'error' : undefined}
                    >
                      <Toggle
                        label='Yes'
                        onChange={val => handleOnChangeGracenote({enabled: val})}
                        value={gracenoteModel.enabled}
                        id='gracenoteToogle'
                      />
                    </FormItem>
                    <FormItem {...form.tmsid} child='Copy'>
                      <Copy text={model.tmsid ?? ''} toCopy={model.tmsid} />
                    </FormItem>
                  </Cluster>
                  <Stack space='small'>
                    <FormItem
                      {...gracenoteForm.dynamicClip}
                      required={gracenoteModel.enabled}
                      permission={permissions.CHANNEL_EDIT}
                    >
                      <TextInput
                        id='dynamicClip'
                        value={gracenoteModel.dynamicClip}
                        onChange={val => handleOnChangeGracenote({dynamicClip: val})}
                      />
                    </FormItem>
                    <Grid maxCols={2} rowGap='small' columnGap='xlarge'>
                      <FormItem
                        {...gracenoteForm.genre}
                        required={gracenoteModel.enabled}
                        permission={permissions.CHANNEL_EDIT}
                      >
                        <Select
                          options={orderBy(
                            (genres || []).map(g => ({label: g.genre})),
                            'label',
                          )}
                          onChange={value => handleOnChangeGracenote({genre: value.label, subGenre: ''})}
                          value={{label: gracenoteModel.genre || ''}}
                          placeholder='Select Genre'
                        />
                      </FormItem>
                      <FormItem
                        {...gracenoteForm.subGenre}
                        required={gracenoteModel.enabled}
                        permission={permissions.CHANNEL_EDIT}
                      >
                        <Select
                          options={subgenresOptions}
                          onChange={value => handleOnChangeGracenote({subGenre: value.label})}
                          value={{label: gracenoteModel.subGenre || ''}}
                          placeholder='Select Sub-Genre'
                        />
                      </FormItem>
                      <FormItem
                        {...gracenoteForm.category}
                        required={gracenoteModel.enabled}
                        permission={permissions.CHANNEL_EDIT}
                      >
                        <Select
                          options={orderBy(
                            (categories || []).map(g => ({label: g.category})),
                            'label',
                          )}
                          placeholder='Select Category'
                          value={{label: gracenoteModel.category || ''}}
                          onChange={value => handleOnChangeGracenote({category: value.label, subCategory: ''})}
                        />
                      </FormItem>
                      <FormItem
                        {...gracenoteForm.subCategory}
                        required={gracenoteModel.enabled}
                        permission={permissions.CHANNEL_EDIT}
                      >
                        <Select
                          options={subCategoriesOptions}
                          onChange={value => handleOnChangeGracenote({subCategory: value.label})}
                          placeholder='Select Sub-Category'
                          value={{label: gracenoteModel.subCategory || ''}}
                        />
                      </FormItem>
                    </Grid>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};
